import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Flex from "../flex";
import MenuItem from "../filter-bar/MenuItem";

const SheetOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const SheetContainer = styled(Flex)`
  background: white;
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 100vw;
  max-height: 80vh;
  flex-direction: column;
  position: relative;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const SheetHeader = styled(Flex)`
  padding: 20px 16px 16px 16px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dee5ec;
  width: 100%;
  border-radius: 8px 8px 0 0;
`;

const SheetTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #424242;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6e6e6e;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    color: #424242;
  }
`;

const OptionsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  width: 100%;
`;

const SaveButtonContainer = styled(Flex)`
  padding: 16px;
  width: 100%;
`;

const SaveButton = styled.button`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: none;
  background: ${({ theme }) => theme.colors.primary500};
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.primary400};
  }

  &:active {
    background: ${({ theme }) => theme.colors.primaryPressed};
  }

  &:disabled {
    background: #dee5ec;
    cursor: not-allowed;
  }
`;

const BottomSheet = ({
  title,
  options,
  selectedValue,
  onValueChange,
  onSave,
  onClose,
  saveButtonText = "Save"
}) => {
  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleItemClick = value => {
    onValueChange(value);
  };

  const handleContainerClick = e => {
    e.stopPropagation();
  };

  return (
    <SheetOverlay onClick={handleOverlayClick} data-bottom-sheet>
      <SheetContainer onClick={handleContainerClick}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <CloseButton onClick={onClose}>{"×"}</CloseButton>
        </SheetHeader>

        <OptionsContainer>
          {options.map((option, index) => (
            <MenuItem
              key={option.value}
              label={option.label}
              value={option.value}
              isSelected={selectedValue === option.value}
              onClick={() => handleItemClick(option.value)}
              isFirst={index === 0}
              isLast={index === options.length - 1}
            />
          ))}
        </OptionsContainer>

        <SaveButtonContainer>
          <SaveButton onClick={onSave}>{saveButtonText}</SaveButton>
        </SaveButtonContainer>
      </SheetContainer>
    </SheetOverlay>
  );
};

// TODO move to TypeScript.  PropTypes are considered legacy. Using PropTypes in the meantime.
BottomSheet.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedValue: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  saveButtonText: PropTypes.string
};

export default BottomSheet;
